import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { YoutubeCompareOverviewSummaryData, YoutubeCompareAccountOverview } from '@anytag/graphql';
import { TableCell } from '@anytag/shared-atoms';
import { InfluencerAvatar } from '@anytag/shared-molecules';
import { AnalyticsCompareOverviewTable } from '../../shared';
import { AnalyticsYoutubeComparableAccountType } from '../types';

export interface AnalyticsCompareYoutubeOverviewTableProps {
  data: YoutubeCompareAccountOverview[];
  comparableAccounts: AnalyticsYoutubeComparableAccountType[];
}

const getNumberGrowthProps = (data?: YoutubeCompareOverviewSummaryData) => ({
  total: data?.total || 0,
  growth: data?.growth || 0,
  rate: data?.percentage || 0
});

export const AnalyticsCompareYoutubeOverviewTable = ({
  data,
  comparableAccounts
}: AnalyticsCompareYoutubeOverviewTableProps) => {
  const { t } = useTranslation();

  const tableData = comparableAccounts.map((account) => ({
    account,
    ...data.find((item) => item.accountId === account.id)?.summary
  }));
  const columns: ColumnDef<(typeof tableData)[number]>[] = [
    {
      accessorKey: 'account',
      meta: { sticky: true },
      header: t<string>('HeaderColumn.Account'),
      cell: ({ row }) => (
        <div className="flex items-center">
          <InfluencerAvatar size="42px" src={row.original.account.avatarUrl} className="rounded-none" />
          <TableCell.Text value={row.original.account.username} className="ml-2" />
        </div>
      )
    },
    {
      accessorKey: 'totalSubscribers',
      header: t<string>('HeaderColumn.Subscribers (Total)'),
      meta: { help: t<string>('Tooltip.compareSubscribersTotal') },
      cell: ({ row }) => <TableCell.NumberGrowth {...getNumberGrowthProps(row.original.totalSubscribers)} />
    },
    {
      accessorKey: 'totalPosts',
      header: t<string>('HeaderColumn.Posts (Total)'),
      meta: { help: t<string>('Tooltip.comparePostsTotal') },
      cell: ({ row }) => <TableCell.NumberGrowth {...getNumberGrowthProps(row.original.totalPosts)} />
    },
    {
      accessorKey: 'averageViews',
      header: t<string>('HeaderColumn.Avg Views'),
      meta: { help: t<string>('Tooltip.compareAvgViews') },
      cell: ({ row }) => <TableCell.NumberGrowth {...getNumberGrowthProps(row.original.averageViews)} />
    },
    {
      accessorKey: 'maximumViews',
      header: t<string>('HeaderColumn.Maximum Views'),
      meta: { help: t<string>('Tooltip.compareMaximumViews') },
      cell: ({ row }) => <TableCell.NumberGrowth {...getNumberGrowthProps(row.original.maximumViews)} />
    },
    {
      accessorKey: 'averageLikes',
      header: t<string>('HeaderColumn.Avg Likes'),
      meta: { help: t<string>('Tooltip.compareAvgLikes') },
      cell: ({ row }) => <TableCell.NumberGrowth {...getNumberGrowthProps(row.original.averageLikes)} />
    },
    {
      accessorKey: 'maximumLikes',
      header: t<string>('HeaderColumn.Maximum Likes'),
      meta: { help: t<string>('Tooltip.compareMaximumLikes') },
      cell: ({ row }) => <TableCell.NumberGrowth {...getNumberGrowthProps(row.original.maximumLikes)} />
    },
    {
      accessorKey: 'averageComments',
      header: t<string>('HeaderColumn.Avg Comments'),
      meta: { help: t<string>('Tooltip.compareAvgComments') },
      cell: ({ row }) => <TableCell.NumberGrowth {...getNumberGrowthProps(row.original.averageComments)} />
    }
  ];

  return comparableAccounts.length ? <AnalyticsCompareOverviewTable data={tableData} columns={columns} /> : null;
};
